/**
 * Phase 18 Ask 4.3 — section-scoped error boundary for Plan library
 * pages. Unlike the top-level <ErrorBoundary>, this one catches a
 * single page's crash WITHOUT collapsing the AppShell. Operators see
 * a recoverable error card with a "Reload page" action; the nav and
 * other Plan library pages remain usable.
 */
import { Component } from "react";
import { AlertOctagon, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default class LibraryErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error(
            `LibraryErrorBoundary[${this.props.pageLabel || "?"}]`,
            error, info,
        );
    }
    render() {
        if (!this.state.error) return this.props.children;
        const msg = this.state.error?.message || String(this.state.error);
        return (
            <div
                className="rounded-md border border-ux-danger/40 bg-ux-danger/10 p-6 text-ux-text-1"
                data-testid="library-error-boundary-fallback"
            >
                <div className="flex items-center gap-2 mb-2">
                    <AlertOctagon className="size-5 text-ux-danger" />
                    <h2 className="text-base font-semibold">
                        {this.props.pageLabel || "This page"} hit an error.
                    </h2>
                </div>
                <p className="text-sm text-ux-text-2 mb-3">
                    The rest of the workbench is still usable — switch
                    pages in the side nav. If this persists, copy the
                    detail below to a Slack/Issue thread.
                </p>
                <pre className="mb-3 max-h-32 overflow-auto rounded bg-ux-bg-0 p-2 text-[11px] text-ux-text-3"
                     data-testid="library-error-boundary-message">
                    {msg}
                </pre>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                    data-testid="library-error-boundary-reload"
                >
                    <RotateCw className="size-3.5 mr-1.5" />
                    Reload page
                </Button>
            </div>
        );
    }
}
